import { DateTime } from 'luxon'
import { MouseEvent, ReactNode, memo, useCallback, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'

import translationKeys from '@translationKeys'
import { Nullable } from '@types'

import { PrivateCompanyIndicator } from '@components/PrivateCompanyIndicator'
import Button from '@components/formControls/Button'
import ProfileAlertButton from '@modules/alerts/components/ProfileAlertButton'
import { ProfileAlert } from '@modules/alerts/types'
import { getProfileIsUpdated } from '@modules/alerts/utils'
import { useAuthContext } from '@modules/auth/hooks/useAuthContext'
import { isP1PlusEcommerceUser } from '@modules/auth/types'
import ContactedBubble from '@modules/chat/components/ContactedBubble'
import ContactsHistoryPopover from '@modules/contacts/components/ContactsHistoryPopover'
import ContactsOverviewCard from '@modules/contacts/components/ContactsOverviewCard'
import { CompanyContactHistoryCounts, Meeting, MeetingStatus, UPCOMING_MEETINGS } from '@modules/contacts/types'
import MarkAsViewedButton from '@modules/savedSearch/components/MarkAsViewedButton'
import { AnyProfile, AnyResultProfile, Delegate, HistoryProfileEvent, ProfileType } from '@modules/search/types'
import ShortlistProfileButton from '@modules/shortlist/components/ShortlistProfileButton'
import { Typography, useMediaQuery, useTheme } from '@mui/material'
import Box from '@mui/material/Box'
import Grid from '@mui/material/Grid'
import { useFeatureFlags } from '@shared/hooks/useFeatureFlags'
import { useScreenDimensions } from '@shared/hooks/useScreenDimensions'
import { createEmptyArray, truncateWithDots } from '@shared/utils'

import FieldSectionRow, { FieldSectionProps } from './FieldSectionRow'
import {
  MainTextWrapper,
  NewItemButtonWrapper,
  ProfileCardContentContainer,
  ProfileCardFooterContainer,
  ProfileCardHeaderContainer,
  ProfileDate,
  ProfileDateBox,
  ProfileHeaderTitle,
  ProfileHeaderTitleContainer,
  ResultCardItemInner,
  ResultCardProfileTitle,
  SecondaryTextWrapper,
  StyledProfileLogo,
  ThirdTextWrapper,
  UpdatedLabel,
} from './ResultProfileList.styles'

/** temp thing, sanitizing will be done on BE */
const sanitizeString = (str: string) =>
  str
    ?.replaceAll(/(<([^>]+)>)/gi, '')
    .replaceAll(/&amp;/g, '&')
    .replaceAll(/&nbsp;/g, ' ')

export interface ResultProfileItemProps {
  resultProfile: AnyResultProfile
  fields: {
    field: string
    label: string
    format?: (value: string | string[]) => string
    render?: (profile: AnyProfile, events?: HistoryProfileEvent[]) => ReactNode | undefined
    id?: string
  }[]
  renderTitle: (profile: AnyProfile) => string
  fieldsGrid: { lg: number[]; md?: number[] }
  onContactClick?: (e: Nullable<MouseEvent<HTMLElement>>) => void
  onReadMoreClick?: () => void
  companyContactCounts?: Nullable<CompanyContactHistoryCounts>
  recentMeeting?: Nullable<Meeting>
  isContactButtonEnabled?: boolean
  hasContacted?: boolean
  isViewed?: boolean
  isNew?: boolean
  savedSearchId?: number
  profileAlert?: ProfileAlert
  readMoreBtnId?: string
}

const ResultProfileItem = (props: ResultProfileItemProps) => {
  const {
    resultProfile,
    companyContactCounts,
    fields,
    renderTitle,
    fieldsGrid,
    onContactClick,
    onReadMoreClick,
    recentMeeting,
    isContactButtonEnabled,
    hasContacted,
    isViewed: rawIsViewed,
    isNew,
    savedSearchId,
    profileAlert,
    readMoreBtnId,
  } = props
  const { t } = useTranslation()
  const { user } = useAuthContext()
  const { isDesktop } = useScreenDimensions()
  const isEcommerce = isP1PlusEcommerceUser(user?.subscriptionLevel || 0)

  const theme = useTheme()
  const { isMobile, isSmallMobile } = useScreenDimensions()
  const isMdScreen = useMediaQuery(theme.breakpoints.down(1250))

  const { hasAccessToChat, hasAccessToAlerts } = useFeatureFlags()
  const { latest: profile, events, latestCompany, hasPublicProfile } = resultProfile
  const profileType = resultProfile['@type']

  const rawIsUpdated = useMemo(
    () =>
      getProfileIsUpdated({
        profile,
        profileAlert,
        type: profileType,
        assetLatestCompany: latestCompany,
        events,
      }),
    [profileType, profileAlert, profile, latestCompany, events],
  )

  const isUpdated = hasAccessToAlerts && rawIsUpdated

  const [messageStreamAnchorRef, setMessageStreamAnchorRef] = useState<Nullable<HTMLElement>>(null)
  const isViewed = rawIsViewed ?? true
  const preparedFields = useMemo(
    () =>
      fields
        ?.map(fieldOptions => {
          const { format, label, render, id } = fieldOptions

          if (render) {
            return {
              value: render(profile, events),
              label,
            }
          }

          const rawValue = profile[fieldOptions.field as keyof AnyProfile] as string
          const formatted = format ? format(rawValue) : rawValue
          const sanitized = sanitizeString(formatted)

          return {
            value: sanitized && truncateWithDots(sanitized, 400),
            id,
            label,
          }
        })
        .filter(i => i.value),
    [fields, profile, events],
  )

  const upcomingMeeting = UPCOMING_MEETINGS.includes(recentMeeting?.status as MeetingStatus) ? recentMeeting : null

  const columns = useMemo(() => {
    const gridData = isMdScreen && fieldsGrid.md ? fieldsGrid.md : fieldsGrid.lg
    const columnsArr = createEmptyArray(gridData.length)

    const { result } = columnsArr.reduce<{ result: FieldSectionProps['fields'][]; indexed: number }>(
      acc => {
        const rows = createEmptyArray(gridData[acc.result.length])
          .map((_, rIdx) => preparedFields[acc.indexed + rIdx])
          .filter(Boolean)

        return {
          result: [...acc.result, rows] as FieldSectionProps['fields'][],
          indexed: acc.indexed + rows.length,
        }
      },
      { result: [], indexed: 0 },
    )

    if ((companyContactCounts || upcomingMeeting) && result.length >= 2) {
      const lastColumnFields = result[result.length - 1]
      const prevColumnFields = result[result.length - 2]

      if (lastColumnFields && prevColumnFields) {
        result[result.length - 2] = [...prevColumnFields, ...lastColumnFields]
        result.pop()
      }
    }

    return result
  }, [preparedFields, isMdScreen, fieldsGrid, companyContactCounts, upcomingMeeting])

  const handleDetailsClick = useCallback<React.MouseEventHandler<HTMLDivElement>>(e => {
    setMessageStreamAnchorRef(e.target as HTMLElement)
  }, [])

  const companyId = profileType === ProfileType.company ? profile.id : (profile as Delegate).companyId
  const contactButtonId = `contact_button-${profileType === ProfileType.delegate ? profileType : ProfileType.company}`
  const showNewInSavedSearchButton = !isViewed && savedSearchId

  const ProfileHeader = (
    <Box display="flex" alignItems="center" gap="5px" sx={{ paddingBottom: isSmallMobile ? '10px' : 0 }}>
      {isSmallMobile && (
        <StyledProfileLogo
          profileData={profile}
          profileType={profileType}
          width={isMobile ? 20 : 43}
          showAltText={false}
          circleView
        />
      )}
      <ProfileHeaderTitle>
        <ResultCardProfileTitle variant="h2" onClick={onReadMoreClick}>
          {renderTitle(profile)}
        </ResultCardProfileTitle>
        {!!profile && profile.isPrivate && <PrivateCompanyIndicator />}
      </ProfileHeaderTitle>
    </Box>
  )
  return (
    <ResultCardItemInner $isPrimary={!isViewed || isUpdated || isNew} data-testid="search-result-item-inner">
      {!showNewInSavedSearchButton && isUpdated && <UpdatedLabel>{t(translationKeys.UPDATED)}</UpdatedLabel>}
      {!showNewInSavedSearchButton && !isUpdated && isNew && <UpdatedLabel>{t(translationKeys.NEW)}</UpdatedLabel>}
      {isSmallMobile && ProfileHeader}
      <ProfileCardHeaderContainer>
        <Box
          display="flex"
          justifyContent="space-between"
          width="100%"
          // sx={{ sm: { flexDirection: 'column-reverse', gap: '5px' } }}
        >
          <ProfileHeaderTitleContainer>
            {!isSmallMobile && (
              <StyledProfileLogo
                profileData={profile}
                profileType={profileType}
                width={isMobile ? 20 : 43}
                showAltText={false}
                circleView
              />
            )}
            <Box sx={{ md: { color: 'green' } }}>
              {!isSmallMobile && ProfileHeader}
              <ProfileDateBox>
                {!isMobile && <Typography fontWeight={500}>{t(translationKeys.SEARCH_BY_PROFILE_UPDATED)}</Typography>}
                <ProfileDate variant="body1">
                  {DateTime.fromISO(profile.updatedAt || profile.createdAt).toFormat('MMM dd, yyyy')}
                </ProfileDate>
              </ProfileDateBox>
            </Box>
          </ProfileHeaderTitleContainer>

          <Box display="flex" ml="10px" alignItems="center">
            {hasAccessToAlerts && (
              <ProfileAlertButton profile={resultProfile} savedProfileAlert={profileAlert} isPrivate={!hasPublicProfile} />
            )}
            <ShortlistProfileButton profile={profile} events={resultProfile.events} location="search_result_profile" />
          </Box>
        </Box>
      </ProfileCardHeaderContainer>

      <ProfileCardContentContainer>
        <Grid
          container
          columns={{
            xs: 1, // on phones 1 virtual column
            sm: 2, // on small tablets 2 virtual columns
            md: 12, // on desktop 12 virtual columns
            lg: 12,
          }}
          wrap={isDesktop ? 'nowrap' : 'wrap'}
          width="100%"
          spacing={2}
        >
          {/* 1st col: always fills 1/1 on xs, 1/2 on sm, 7/12 on md, 5/12 on lg */}
          <Grid size={{ xs: 1, sm: 1, md: 4, lg: 5 }}>
            <FieldSectionRow wrapper={MainTextWrapper} fields={columns[0]} />
          </Grid>

          {/* 2nd col: same 1/1 on xs, 1/2 on sm, 5/12 on md, 4/12 on lg */}
          <Grid size={{ xs: 1, sm: 1, md: 4, lg: 4 }}>
            <FieldSectionRow wrapper={SecondaryTextWrapper} fields={columns[1]} />
          </Grid>

          {/* 3rd col: full-width row on xs, spans both cols on sm, 3/12 on lg */}
          <Grid
            size={{ xs: 1, sm: 2, md: 2, lg: 3 }}
            sx={{
              display: 'block',
            }}
          >
            {companyContactCounts || upcomingMeeting ? (
              <ContactsOverviewCard
                companyId={companyId}
                counts={companyContactCounts}
                meeting={upcomingMeeting}
                onDetailsClick={handleDetailsClick}
                isHighlighted={hasAccessToAlerts && !!profileAlert?.hasNewActivity}
              />
            ) : (
              <FieldSectionRow wrapper={ThirdTextWrapper} fields={columns[2]} />
            )}
          </Grid>
        </Grid>
      </ProfileCardContentContainer>

      <ProfileCardFooterContainer>
        <Button
          id={readMoreBtnId || 'search_card-read_more'}
          variant="outlined"
          onClick={onReadMoreClick}
          data-testid="view_full_profile_btn"
        >
          {t(translationKeys.SEARCH_RESULTS_ITEM_READ_MORE_BTN)}
        </Button>
        {isContactButtonEnabled && hasAccessToChat && hasPublicProfile && !isEcommerce && (
          <ContactedBubble hasContacted={hasContacted}>
            <Button variant="contained" onClick={onContactClick} data-testid="contact_profile_btn" id={contactButtonId}>
              {t(translationKeys.CONTACT_BUTTON)}
            </Button>
          </ContactedBubble>
        )}
      </ProfileCardFooterContainer>

      {recentMeeting && messageStreamAnchorRef && (
        <ContactsHistoryPopover
          companyId={companyId}
          recentMeeting={recentMeeting}
          anchor={messageStreamAnchorRef}
          setAnchor={setMessageStreamAnchorRef}
        />
      )}
      {showNewInSavedSearchButton && (
        <NewItemButtonWrapper>
          <MarkAsViewedButton savedSearchId={savedSearchId} profileId={profile.id} />
        </NewItemButtonWrapper>
      )}
    </ResultCardItemInner>
  )
}

export default memo(ResultProfileItem)
